import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './HeroTransition.css';

import supermanImg from '../assets/heroes/superman.png';
import flashImg from '../assets/heroes/flash.png';

const HeroTransition = ({ transitionType, children, onComplete }) => {
  const [showHero, setShowHero] = useState(true);
  const [screenSize, setScreenSize] = useState('desktop');
  const onCompleteRef = useRef(onComplete);

  // Update ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Detect screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const useImages = true;

  const heroImages = {
    superman: supermanImg,
    flash: flashImg
  };

  useEffect(() => {
    // Animation durations that match the framer-motion transition durations
    // Superman: 3.0s base * speedMultiplier
    // Flash: 1.8s base
    const speedMultiplier = screenSize === 'desktop' ? 1 : screenSize === 'tablet' ? 1.1 : 1.2;
    
    let animationDuration;
    if (transitionType === 'superman') {
      animationDuration = 3000 * speedMultiplier;
    } else if (transitionType === 'flash') {
      animationDuration = screenSize === 'desktop' ? 1800 : screenSize === 'tablet' ? 2000 : 2200;
    } else {
      animationDuration = 3000; // Default
    }
    
    // Add buffer time for the animation to complete
    const totalDuration = animationDuration + 200; // Extra 200ms buffer
    
    console.log('HeroTransition: Starting', transitionType, 'animation with duration', totalDuration, 'ms');
    const timer = setTimeout(() => {
      console.log('HeroTransition: Animation complete, hiding hero');
      setShowHero(false);
    }, totalDuration);

    return () => clearTimeout(timer);
  }, [screenSize, transitionType]);

  // Call onComplete when transition finishes
  useEffect(() => {
    if (!showHero && onCompleteRef.current) {
      // Add a small delay to allow exit animation to complete
      const completeTimer = setTimeout(() => {
        console.log('HeroTransition: Calling onComplete callback');
        try {
          onCompleteRef.current();
        } catch (error) {
          console.error('HeroTransition: Error calling onComplete:', error);
        }
      }, 300); // Match the exit transition duration
      return () => clearTimeout(completeTimer);
    }
  }, [showHero]); // Remove onComplete from dependencies

  // Failsafe: Force callback after maximum time
  useEffect(() => {
    const maxWaitTime = 5000; // 5 seconds maximum
    const failsafeTimer = setTimeout(() => {
      if (showHero && onCompleteRef.current) {
        console.warn('HeroTransition: Failsafe triggered - forcing navigation');
        onCompleteRef.current();
      }
    }, maxWaitTime);
    
    return () => clearTimeout(failsafeTimer);
  }, [showHero]);

  const getTransitionAnimation = () => {
    // Optimize based on screen size
    const heroScale = screenSize === 'mobile' ? 0.6 : screenSize === 'tablet' ? 0.8 : 1;
    
    // Slower speed for better visibility
    const speedMultiplier = screenSize === 'desktop' ? 1 : screenSize === 'tablet' ? 1.1 : 1.2;

    switch (transitionType) {
      case 'superman':
        return {
          hero: (
            <motion.div
              className="superman-hero hero-image-wrapper"
              initial={{ y: '110vh', x: '-50%', scale: heroScale }}
              animate={{ y: '-110vh', x: '-50%', scale: heroScale }}
              transition={{ 
                duration: 3.0 * speedMultiplier,
                ease: [0.43, 0.13, 0.23, 0.96]
              }}
              style={{
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                perspective: 1000,
                WebkitPerspective: 1000
              }}
            >
              {useImages && heroImages.superman ? (
                <img 
                  src={heroImages.superman} 
                  alt="Superman" 
                  className="hero-img"
                  style={{
                    willChange: 'transform',
                    backfaceVisibility: 'hidden'
                  }}
                />
              ) : (
                '🦸‍♂️'
              )}
            </motion.div>
          ),
          overlay: {
            initial: { y: '100vh' },
            animate: { y: 0 },
            transition: { 
              duration: 3.0 * speedMultiplier,
              ease: [0.43, 0.13, 0.23, 0.96]
            }
          },
          curtain: {
            initial: { y: '100vh' },
            animate: { y: 0 },
            transition: { 
              duration: 3.0 * speedMultiplier,
              ease: [0.43, 0.13, 0.23, 0.96]
            }
          }
        };

      case 'flash':
        const flashDuration = screenSize === 'desktop' ? 1.8 : screenSize === 'tablet' ? 2.0 : 2.2;
        const flashStartX = screenSize === 'desktop' ? '-25vw' : screenSize === 'tablet' ? '-35vw' : '-40vw';
        const flashEndX = '120vw';

        return {
          hero: (
            <>
              <motion.div
                className="flash-hero hero-image-wrapper"
                initial={{ x: flashStartX, y: '-50%', scale: heroScale }}
                animate={{ x: flashEndX, y: '-50%', scale: heroScale }}
                transition={{ 
                  duration: flashDuration, 
                  ease: 'linear'
                }}
                style={{
                  willChange: 'transform',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  perspective: 1000,
                  top: '45%'
                }}
              >
                {useImages && heroImages.flash ? (
                  <img 
                    src={heroImages.flash} 
                    alt="Flash" 
                    className="hero-img"
                    style={{
                      willChange: 'transform',
                      backfaceVisibility: 'hidden'
                    }}
                  />
                ) : (
                  '⚡'
                )}
              </motion.div>
              <motion.div
                className="speed-lines"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 1, 0] }}
                transition={{ 
                  duration: flashDuration, 
                  times: [0, 0.1, 0.9, 1]
                }}
                style={{
                  willChange: 'transform, opacity'
                }}
              />
            </>
          ),
          overlay: {
            initial: { x: '-100vw' },
            animate: { x: 0 },
            transition: { 
              duration: flashDuration, 
              ease: 'linear'
            }
          },
          curtain: {
            initial: { x: '-100vw' },
            animate: { x: 0 },
            transition: { 
              duration: flashDuration, 
              ease: 'linear'
            }
          }
        };

      default:
        return {
          hero: null,
          overlay: {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            transition: { duration: 0.5 }
          },
          curtain: {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            transition: { duration: 0.5 }
          }
        };
    }
  };

  const animation = getTransitionAnimation();

  return (
    <div className="hero-transition-container">
      {/* Dark curtain that reveals the new page - only when transitioning to new content */}
      {children && (
        <motion.div
          className="transition-curtain"
          initial={animation.curtain.initial}
          animate={animation.curtain.animate}
          transition={animation.curtain.transition}
        />
      )}

      {/* The new page content that slides in */}
      {children && (
        <motion.div
          className="new-page-content"
          initial={animation.overlay.initial}
          animate={animation.overlay.animate}
          transition={animation.overlay.transition}
        >
          {children}
        </motion.div>
      )}

      {/* Hero character overlay */}
      <AnimatePresence>
        {showHero && (
          <motion.div
            className="transition-overlay"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {animation.hero}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HeroTransition;